import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { TOUR_CONFIGS, TRAINING_MODULES, getTourConfigForRoute } from "./tourConfigs";

// Re-export tour data for back-compat with existing imports.
export { TOUR_CONFIGS, TRAINING_MODULES, getTourConfigForRoute };

// ============== CONTEXT ==============
const TourContext = createContext(null);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
};

export const TourProvider = ({ children }) => {
  const location = useLocation();
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(1);
  const [currentTourId, setCurrentTourId] = useState(null);
  const [stageTourOverride, setStageTourOverride] = useState(null);
  const [targetRect, setTargetRect] = useState(null);
  const [showTrainingPanel, setShowTrainingPanel] = useState(false);
  const [showModulesDrawer, setShowModulesDrawer] = useState(false);
  const lastClickBeforeRef = useRef(null);

  // Get current page's tour config — prefer the stage-set override (set by the
  // active pipeline stage component), then a manually started tour, then route.
  const currentPageTourId = stageTourOverride || getTourConfigForRoute(location.pathname);
  const currentTourConfig = TOUR_CONFIGS[currentTourId || currentPageTourId];
  const currentStepData = currentTourConfig?.steps?.[tourStep - 1];
  const totalSteps = currentTourConfig?.steps?.length || 0;

  // Check for first visit
  useEffect(() => {
    const hasSeenTour = localStorage.getItem(`arq_tour_${currentPageTourId}_completed`);
    const hasSeenAnyTour = localStorage.getItem("arq_tour_seen");
    
    // Show training panel on first ever visit to any page
    if (!hasSeenAnyTour) {
      setShowTrainingPanel(true);
      localStorage.setItem("arq_tour_seen", "true");
    }
  }, [currentPageTourId]);

  // Update spotlight position. If the step has `clickBefore`, click that element
  // first (e.g. a sub-step pill) so the target becomes visible.
  const updateSpotlight = useCallback(() => {
    if (!currentStepData?.targetSelector) {
      setTargetRect(null);
      return;
    }

    const clickBefore = currentStepData.clickBefore;
    if (clickBefore && lastClickBeforeRef.current !== `${tourStep}:${clickBefore}`) {
      const trigger = document.querySelector(clickBefore);
      if (trigger) {
        trigger.click();
        lastClickBeforeRef.current = `${tourStep}:${clickBefore}`;
        // Allow the click-driven render to settle before we look for the target.
        setTimeout(updateSpotlight, 200);
        return;
      }
    }

    const element = document.querySelector(currentStepData.targetSelector);
    if (element) {
      // Scroll element into view if it's outside the viewport
      const rect = element.getBoundingClientRect();
      const isOutOfView = rect.top < 0 || rect.bottom > window.innerHeight || rect.left < 0 || rect.right > window.innerWidth;
      if (isOutOfView) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        // Re-read rect after scroll settles
        setTimeout(() => {
          setTargetRect(element.getBoundingClientRect());
        }, 400);
        return;
      }
      setTargetRect(rect);
    } else {
      setTargetRect(null);
    }
  }, [currentStepData, tourStep]);

  useEffect(() => {
    if (showTour) {
      // Small delay to let DOM settle
      const timer = setTimeout(updateSpotlight, 100);
      window.addEventListener("resize", updateSpotlight);
      window.addEventListener("scroll", updateSpotlight);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", updateSpotlight);
        window.removeEventListener("scroll", updateSpotlight);
      };
    }
  }, [showTour, tourStep, updateSpotlight]);

  const startTour = (tourId = null) => {
    const id = tourId || currentPageTourId;
    setCurrentTourId(id);
    setShowTrainingPanel(false);
    setShowModulesDrawer(false);
    setTourStep(1);
    lastClickBeforeRef.current = null;
    setTimeout(() => {
      setShowTour(true);
    }, 100);
  };

  const nextStep = () => {
    if (tourStep === totalSteps) {
      endTour(true);
    } else {
      lastClickBeforeRef.current = null;
      setTourStep(tourStep + 1);
    }
  };

  const prevStep = () => {
    if (tourStep > 1) {
      lastClickBeforeRef.current = null;
      setTourStep(tourStep - 1);
    }
  };

  const endTour = (completed = false) => {
    setShowTour(false);
    setTargetRect(null);
    if (completed && currentTourId) {
      localStorage.setItem(`arq_tour_${currentTourId}_completed`, "true");
    }
    if (!completed) {
      setShowTrainingPanel(true);
    }
    setCurrentTourId(null);
  };

  const openTrainingPanel = () => {
    setShowModulesDrawer(false);
    setShowTrainingPanel(true);
  };

  const closeTrainingPanel = () => {
    setShowTrainingPanel(false);
  };

  const openModulesDrawer = () => {
    setShowModulesDrawer(true);
  };

  const closeModulesDrawer = () => {
    setShowModulesDrawer(false);
  };

  const value = {
    // State
    showTour,
    tourStep,
    totalSteps,
    targetRect,
    currentStepData,
    currentTourConfig,
    currentPageTourId,
    showTrainingPanel,
    showModulesDrawer,
    // Actions
    startTour,
    nextStep,
    prevStep,
    endTour,
    openTrainingPanel,
    closeTrainingPanel,
    openModulesDrawer,
    closeModulesDrawer,
    setStageTour: setStageTourOverride,
    clearStageTour: () => setStageTourOverride(null),
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

export default TourContext;
